import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, X, Copy, Twitter, Facebook, Trophy, Check, PartyPopper } from "lucide-react";
import { formatGBP } from "@/lib/marketplace";

/**
 * KidCelebrationModal — first-sale + earnings-milestone celebration.
 *
 * Modernised port of the legacy `CelebrationModal` (US-branded, orphaned)
 * into the active Kid Zone workspace. Uses:
 *   - GBP (£) currency via `formatGBP`
 *   - Canonical share URL emitted by the backend (`payload.share_url`)
 *   - "MyTableTopApp" branding across share text
 *   - Confetti burst, X/Twitter + Facebook share, copy link, Keep selling
 *     primary CTA
 *
 * Dismisses via `POST /api/kid/celebrations/ack` so each badge only
 * ever shows once per kid.
 */
const BADGE_INFO = {
  "first-sale": {
    title: "First sale!",
    subtitle: "You just made your very first sale on MyTableTopApp.",
    headline: "I just made my first sale on MyTableTopApp!",
    emoji: "\u{1F947}",  // gold medal 🥇
    accent: "from-amber-100 via-white to-rose-50",
    pillClass: "bg-amber-100 text-amber-800",
    tier: "Badge Unlocked",
  },
  "milestone-50": {
    title: "\u00A350 Earned!",  // £50
    subtitle: "You've earned your first £50 selling on MyTableTopApp.",
    headline: "I just earned £50 selling on MyTableTopApp!",
    emoji: "\u{1F4B0}",  // 💰
    accent: "from-emerald-100 via-white to-teal-50",
    pillClass: "bg-emerald-100 text-emerald-800",
    tier: "Milestone Unlocked",
  },
  "milestone-100": {
    title: "\u00A3100 Earned!",  // £100
    subtitle: "Triple-digit territory — you've earned £100 selling on MyTableTopApp!",
    headline: "I just hit £100 in sales on MyTableTopApp!",
    emoji: "\u{1F31F}",  // 🌟
    accent: "from-sky-100 via-white to-indigo-50",
    pillClass: "bg-sky-100 text-sky-800",
    tier: "Milestone Unlocked",
  },
  "milestone-200": {
    title: "\u00A3200+ Master Seller!",  // £200+
    subtitle: "You're officially a MyTableTopApp Master Seller. Your buyers love what you make!",
    headline: "I just reached £200+ in sales on MyTableTopApp — Master Seller unlocked!",
    emoji: "\u{1F3C6}",  // 🏆
    accent: "from-fuchsia-100 via-white to-amber-50",
    pillClass: "bg-fuchsia-100 text-fuchsia-800",
    tier: "Master Tier Unlocked",
  },
};

export function KidCelebrationModal({ badge, lastSold, kidAlias, shareUrl, onClose }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const info = BADGE_INFO[badge] || {
    title: "New badge!",
    subtitle: "Keep going!",
    headline: "I unlocked a badge on MyTableTopApp!",
    emoji: "\u{1F3C6}",
    accent: "from-slate-100 via-white to-slate-50",
    pillClass: "bg-slate-100 text-slate-800",
    tier: "Badge Unlocked",
  };

  const priceGbp = Number(lastSold?.price_gbp ?? (lastSold?.price_pennies || 0) / 100);
  const shareText = `${info.headline}${lastSold?.title ? ` — sold "${lastSold.title}" for ${formatGBP(priceGbp)}.` : ""} \u{1F389}`;
  const url = shareUrl || (typeof window !== "undefined" ? window.location.origin : "https://mytabletopapp.co.uk");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard denied — silently fail */ }
  };

  const tweetHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`;

  const ack = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.post("/kid/celebrations/ack", { badge }); } catch (_) { /* still dismiss client-side */ }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      data-testid="kid-celebration-modal"
      onClick={ack}
    >
      <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        {/* Confetti dots */}
        <div className="pointer-events-none absolute -inset-8 overflow-hidden">
          {Array.from({ length: 24 }).map((_, i) => (
            <span
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                left: `${(i * 37) % 100}%`,
                top: `${(i * 53) % 100}%`,
                background: ["#fbbf24", "#3b82f6", "#f43f5e", "#10b981", "#a855f7"][i % 5],
                animation: `kcm-confetti-fall 2.4s ${i * 0.05}s ease-out infinite`,
              }}
            />
          ))}
        </div>

        <div className={`relative card-base p-8 md:p-10 text-center bg-gradient-to-br ${info.accent}`} data-testid="kid-celebration-card">
          <button
            type="button"
            onClick={ack}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            data-testid="kid-celebration-close"
            aria-label="Dismiss celebration"
          >
            <X className="w-4 h-4 text-slate-700" />
          </button>

          <div className="text-6xl mb-3 animate-bounce" data-testid="kid-celebration-emoji">{info.emoji}</div>
          <div className={`pill mx-auto mb-3 ${info.pillClass}`} data-testid="kid-celebration-tier-pill">
            <Trophy className="w-3 h-3 mr-1" /> {info.tier}
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-brandSlate" data-testid="kid-celebration-title">
            {info.title}
          </h2>
          <p className="text-slate-600 mt-2" data-testid="kid-celebration-subtitle">{info.subtitle}</p>

          {/* Sale details card */}
          {lastSold && lastSold.title && (
            <div
              className="mt-6 mx-auto max-w-sm bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 text-left shadow-soft"
              data-testid="kid-celebration-sale-card"
            >
              {lastSold.photo ? (
                <img src={lastSold.photo} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-brandTeal/15 text-brandTeal inline-flex items-center justify-center flex-shrink-0">
                  <PartyPopper className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider" data-testid="kid-celebration-alias">
                  {kidAlias || "You"} just sold
                </div>
                <div className="font-display text-lg font-medium text-brandSlate truncate" data-testid="kid-celebration-item-title">
                  {lastSold.title}
                </div>
              </div>
              <div className="font-display text-xl text-brandTeal font-semibold" data-testid="kid-celebration-item-price">
                {formatGBP(priceGbp)}
              </div>
            </div>
          )}

          {/* Share row */}
          <div className="mt-7">
            <div className="text-sm font-bold text-slate-700 mb-3 flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Share the moment
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <a
                href={tweetHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="kid-celebration-share-x"
              >
                <Twitter className="w-4 h-4" /> X / Twitter
              </a>
              <a
                href={fbHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="kid-celebration-share-fb"
              >
                <Facebook className="w-4 h-4" /> Facebook
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="kid-celebration-share-copy"
              >
                {copied ? (
                  <><Check className="w-4 h-4 text-emerald-600" /> Copied!</>
                ) : (
                  <><Copy className="w-4 h-4" /> Copy link</>
                )}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={ack}
            disabled={busy}
            className="mt-7 mx-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brandPink text-white font-display font-semibold shadow-md hover:bg-brandPink/90 disabled:opacity-60 transition-colors"
            data-testid="kid-celebration-continue"
          >
            Keep selling <PartyPopper className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes kcm-confetti-fall {
          0% { transform: translateY(-30px) rotate(0deg); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translateY(280px) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default KidCelebrationModal;
